import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE } from "@/lib/constants";
import { PaperPlaneTilt, Warning, ArrowClockwise, DownloadSimple, ChartLineUp, CursorClick, Eye, AppleLogo } from "@phosphor-icons/react";
import { StatCard, FieldGroup, formatDate } from "./shared";

export function AnalyticsSection({ data, token, onRefreshInstalls, refreshingInstalls, onSendDigestPreview }) {
  const pv = data?.page_views ?? 0;
  const dc = data?.download_clicks ?? 0;
  const gp = data?.google_play_clicks ?? 0;
  const conv = data?.conversion_rate_pct ?? 0;
  const bySource = data?.by_source ?? {};
  const daily = data?.daily_download_clicks ?? [];
  const topReferrers = data?.top_referrers ?? [];
  const topUtm = data?.top_utm_sources ?? [];
  const heatmap = data?.download_heatmap ?? [];
  const installs = data?.installs ?? null;
  const maxDaily = Math.max(1, ...daily.map((d) => d.count));

  // Order sources for stable display
  const sources = Object.entries(bySource).sort(
    (a, b) => (b[1].download_click || 0) - (a[1].download_click || 0)
  );

  return (
    <section data-testid="admin-analytics" className="space-y-6">
      {/* Real App Store installs from Apple */}
      <InstallsSection
        installs={installs}
        onRefresh={onRefreshInstalls}
        refreshing={refreshingInstalls}
        onSendDigestPreview={onSendDigestPreview}
      />

      <div className="flex items-baseline justify-between pt-4 border-t border-white/10">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#FF2A2A] mb-1">
            // site analytics
          </div>
          <h3 className="font-display text-xl font-extrabold tracking-tight">
            Link clicks
          </h3>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
          who clicked the download buttons on stableish.me
        </span>
      </div>

      {/* Stat cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<Eye size={20} weight="duotone" />}
          label="Page views"
          value={pv}
          testId="analytics-pageviews"
        />
        <StatCard
          icon={<CursorClick size={20} weight="duotone" />}
          label="App Store link clicks"
          value={dc}
          valueClass="text-white"
          testId="analytics-downloads"
          accent={dc > 0}
        />
        <StatCard
          icon={<CursorClick size={20} weight="duotone" />}
          label="Google Play clicks"
          value={gp}
          valueClass="text-white/70"
          testId="analytics-googleplay"
        />
        <div
          data-testid="analytics-conversion"
          className="border border-white/10 bg-[#0a0a0a] p-6"
        >
          <div className="flex items-center gap-2 text-[#FF2A2A] mb-3">
            <ChartLineUp size={20} weight="duotone" />
          </div>
          <div className="font-display text-4xl font-extrabold tracking-tight text-white">
            {conv}
            <span className="text-2xl text-white/60">%</span>
          </div>
          <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
            Conversion rate
          </div>
        </div>
      </div>

      {/* Sources + daily chart */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* By source */}
        <div className="border border-white/10 bg-[#0a0a0a] p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 mb-4">
            Clicks by source
          </div>
          {sources.length === 0 ? (
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/30">
              No clicks tracked yet
            </p>
          ) : (
            <ul className="space-y-3">
              {sources.map(([src, counts]) => {
                const total = counts.download_click || 0;
                const pct = dc > 0 ? Math.round((total / dc) * 100) : 0;
                return (
                  <li key={src} data-testid={`analytics-src-${src}`}>
                    <div className="flex items-baseline justify-between mb-1.5">
                      <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/80">
                        {src}
                      </span>
                      <span className="font-mono text-xs text-white/60">
                        {total} <span className="text-white/35">· {pct}%</span>
                      </span>
                    </div>
                    <div className="h-1.5 bg-white/5 overflow-hidden">
                      <div
                        className="h-full bg-[#FF2A2A]"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Daily chart */}
        <div className="border border-white/10 bg-[#0a0a0a] p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 mb-4">
            Downloads · last 14 days
          </div>
          {daily.length === 0 ? (
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/30">
              No downloads tracked yet
            </p>
          ) : (
            <div className="flex items-end gap-1 h-32">
              {daily.map((d) => {
                const h = Math.max(4, (d.count / maxDaily) * 100);
                return (
                  <div
                    key={d.date}
                    className="flex-1 flex flex-col items-center gap-1 group"
                    title={`${d.date}: ${d.count}`}
                  >
                    <div className="w-full flex items-end h-24">
                      <div
                        className="w-full bg-[#FF2A2A] group-hover:bg-[#FF4D4D] transition-colors"
                        style={{ height: `${h}%` }}
                      />
                    </div>
                    <span className="font-mono text-[8px] text-white/35">
                      {d.date.slice(5)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Traffic sources: referrers + UTM */}
      <div className="grid md:grid-cols-2 gap-4">
        <div
          className="border border-white/10 bg-[#0a0a0a] p-6"
          data-testid="analytics-referrers"
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 mb-4">
            Traffic sources · where visitors came from
          </div>
          {topReferrers.length === 0 ? (
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/30">
              No traffic tracked yet
            </p>
          ) : (
            <ul className="space-y-3">
              {(() => {
                const total = topReferrers.reduce((s, r) => s + r.count, 0);
                return topReferrers.map((r) => {
                  const pct = total > 0 ? Math.round((r.count / total) * 100) : 0;
                  const isDirect = r.host === "direct";
                  const isInternal = r.host === "internal";
                  const label = isDirect
                    ? "direct / no referrer"
                    : isInternal
                      ? "internal navigation"
                      : r.host;
                  return (
                    <li key={r.host} data-testid={`referrer-${r.host}`}>
                      <div className="flex items-baseline justify-between mb-1.5 gap-3">
                        <span
                          className={`text-xs truncate ${
                            isDirect || isInternal
                              ? "font-mono uppercase tracking-[0.18em] text-white/50"
                              : "text-white/85"
                          }`}
                        >
                          {label}
                        </span>
                        <span className="font-mono text-xs text-white/60 shrink-0">
                          {r.count}{" "}
                          <span className="text-white/35">· {pct}%</span>
                        </span>
                      </div>
                      <div className="h-1.5 bg-white/5 overflow-hidden">
                        <div
                          className={`h-full ${isDirect || isInternal ? "bg-white/30" : "bg-[#FF2A2A]"}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </li>
                  );
                });
              })()}
            </ul>
          )}
        </div>

        <div
          className="border border-white/10 bg-[#0a0a0a] p-6"
          data-testid="analytics-utm"
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 mb-4">
            Campaign UTM sources
          </div>
          {topUtm.length === 0 ? (
            <div className="space-y-2">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/30">
                No UTM tags detected yet
              </p>
              <p className="text-xs text-white/45 leading-relaxed">
                Tip: add{" "}
                <code className="font-mono text-[11px] text-[#FF2A2A]">
                  ?utm_source=instagram&utm_medium=bio
                </code>{" "}
                to links you share on social so you can see which posts drive
                the most downloads.
              </p>
            </div>
          ) : (
            <ul className="space-y-3">
              {(() => {
                const total = topUtm.reduce((s, u) => s + u.count, 0);
                return topUtm.map((u) => {
                  const pct = total > 0 ? Math.round((u.count / total) * 100) : 0;
                  return (
                    <li key={u.source} data-testid={`utm-${u.source}`}>
                      <div className="flex items-baseline justify-between mb-1.5 gap-3">
                        <span className="text-xs text-white/85 truncate">
                          {u.source}
                        </span>
                        <span className="font-mono text-xs text-white/60 shrink-0">
                          {u.count}{" "}
                          <span className="text-white/35">· {pct}%</span>
                        </span>
                      </div>
                      <div className="h-1.5 bg-white/5 overflow-hidden">
                        <div
                          className="h-full bg-[#FF2A2A]"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </li>
                  );
                });
              })()}
            </ul>
          )}
        </div>
      </div>

      {/* Best-time-to-post heatmap */}
      <DownloadHeatmap heatmap={heatmap} initialDays={data?.download_heatmap_days ?? 30} token={token} />

      {/* Campaign URL builder */}
      <CampaignUrlBuilder />
    </section>
  );
}

/**
 * DownloadHeatmap — 7 × 24 grid (day-of-week × hour).
 * Backend gives us UTC buckets; frontend shifts them client-side into the
 * selected US timezone so the peak makes intuitive sense for the user.
 */
function DownloadHeatmap({ heatmap: initialHeatmap, initialDays, token }) {
  const [tz, setTz] = useState("America/New_York");
  const [days, setDays] = useState(initialDays);
  const [heatmap, setHeatmap] = useState(initialHeatmap);
  const [loadingRange, setLoadingRange] = useState(false);

  // If the parent re-fetches analytics (e.g. token change), the initial
  // 30-day payload updates — mirror it back into local state so the
  // heatmap reflects the freshest data at the default range.
  useEffect(() => {
    setHeatmap(initialHeatmap);
    setDays(initialDays);
  }, [initialHeatmap, initialDays]);

  const rangeOptions = [
    { value: 7, label: "7d" },
    { value: 30, label: "30d" },
    { value: 90, label: "90d" },
  ];

  const changeRange = async (newDays) => {
    if (newDays === days || loadingRange) return;
    setDays(newDays);
    setLoadingRange(true);
    try {
      const { data } = await axios.get(
        `${API_BASE}/admin/analytics?heatmap_days=${newDays}`,
        { headers: { "X-Admin-Token": token } }
      );
      setHeatmap(data.download_heatmap || []);
    } catch {
      toast.error(`Couldn't load ${newDays}-day heatmap`);
      setDays(days);
    } finally {
      setLoadingRange(false);
    }
  };

  const timezones = [
    { key: "UTC", tz: "UTC", label: "UTC" },
    { key: "ET", tz: "America/New_York", label: "Eastern" },
    { key: "CT", tz: "America/Chicago", label: "Central" },
    { key: "MT", tz: "America/Denver", label: "Mountain" },
    { key: "PT", tz: "America/Los_Angeles", label: "Pacific" },
  ];

  // Compute current UTC → target-tz hour offset via Intl. Handles DST.
  const getOffset = (targetTz) => {
    if (targetTz === "UTC") return 0;
    try {
      const now = new Date();
      const utcHour = now.getUTCHours();
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: targetTz,
        hour: "numeric",
        hour12: false,
      }).formatToParts(now);
      const localHour = parseInt(
        parts.find((p) => p.type === "hour")?.value ?? "0",
        10
      );
      // Range fix: e.g. 23 UTC vs 18 local → offset -5, or 2 UTC vs 21 prev-day local → offset -5
      let diff = localHour - utcHour;
      if (diff > 12) diff -= 24;
      if (diff < -12) diff += 24;
      return diff;
    } catch {
      return 0;
    }
  };

  const offset = getOffset(tz);

  // Build a dense 7×24 UTC grid first
  const utcGrid = Array.from({ length: 7 }, () => Array(24).fill(0));
  heatmap.forEach((h) => {
    const i = h.dow - 1;
    const j = h.hour;
    if (i >= 0 && i < 7 && j >= 0 && j < 24) utcGrid[i][j] = h.count;
  });

  // Shift into selected tz: each UTC cell (dow, hour) moves to
  // (dow', hour') where hour' = (hour + offset) mod 24 and dow shifts if wrap
  const shifted = Array.from({ length: 7 }, () => Array(24).fill(0));
  utcGrid.forEach((row, dowUtc) => {
    row.forEach((count, hourUtc) => {
      if (count === 0) return;
      let newHour = hourUtc + offset;
      let dayShift = 0;
      while (newHour < 0) {
        newHour += 24;
        dayShift -= 1;
      }
      while (newHour >= 24) {
        newHour -= 24;
        dayShift += 1;
      }
      const newDow = (dowUtc + dayShift + 7) % 7;
      shifted[newDow][newHour] += count;
    });
  });

  let max = 0;
  shifted.forEach((row) => row.forEach((c) => { if (c > max) max = c; }));

  // First-match peak (deterministic left-to-right scan)
  let peakDow = null;
  let peakHour = null;
  shifted.forEach((row, i) => {
    row.forEach((c, j) => {
      if (c === max && max > 0 && peakDow === null) {
        peakDow = i;
        peakHour = j;
      }
    });
  });

  const dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const totalClicks = shifted.flat().reduce((a, b) => a + b, 0);
  const tzLabel = timezones.find((t) => t.tz === tz)?.key ?? "UTC";

  return (
    <div
      className="border border-white/10 bg-[#0a0a0a] p-6"
      data-testid="analytics-heatmap"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-1">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
          Best time to post · downloads by hour
          {loadingRange ? (
            <span className="ml-2 text-[#FF2A2A]">· loading…</span>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div
            className="flex items-center gap-1"
            data-testid="heatmap-range-selector"
          >
            {rangeOptions.map((r) => (
              <button
                key={r.value}
                onClick={() => changeRange(r.value)}
                disabled={loadingRange}
                data-testid={`heatmap-range-${r.value}`}
                title={`Last ${r.value} days`}
                className={`font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-1 border transition-colors ${
                  days === r.value
                    ? "bg-[#FF2A2A] border-[#FF2A2A] text-white"
                    : "border-white/15 text-white/60 hover:border-white/40 hover:text-white"
                } disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <div className="w-px h-4 bg-white/10" aria-hidden />
          <div
            className="flex items-center gap-1"
            data-testid="heatmap-tz-selector"
          >
            {timezones.map((t) => (
              <button
                key={t.key}
                onClick={() => setTz(t.tz)}
                data-testid={`heatmap-tz-${t.key}`}
                title={t.label}
                className={`font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-1 border transition-colors ${
                  tz === t.tz
                    ? "bg-[#FF2A2A] border-[#FF2A2A] text-white"
                    : "border-white/15 text-white/60 hover:border-white/40 hover:text-white"
                }`}
              >
                {t.key}
              </button>
            ))}
          </div>
        </div>
      </div>

      {totalClicks === 0 ? (
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/30 mt-4">
          No downloads tracked yet · come back after you&apos;ve driven some traffic
        </p>
      ) : (
        <>
          <div className="mt-5 overflow-x-auto">
            <div className="min-w-[720px]">
              {/* Hour axis labels */}
              <div className="flex ml-10">
                {Array.from({ length: 24 }, (_, h) => (
                  <div
                    key={h}
                    className="flex-1 text-center font-mono text-[8px] text-white/30"
                  >
                    {h % 3 === 0 ? h : ""}
                  </div>
                ))}
              </div>
              {/* Grid rows */}
              {dayLabels.map((day, i) => (
                <div key={day} className="flex items-center mt-1">
                  <div className="w-10 font-mono text-[9px] uppercase tracking-[0.15em] text-white/45">
                    {day}
                  </div>
                  <div className="flex flex-1 gap-[2px]">
                    {shifted[i].map((count, j) => {
                      const intensity = max > 0 ? count / max : 0;
                      const isPeak = i === peakDow && j === peakHour && count > 0;
                      return (
                        <div
                          key={j}
                          title={`${day} ${String(j).padStart(2, "0")}:00 ${tzLabel} · ${count} download${count === 1 ? "" : "s"}`}
                          data-testid={`heatmap-cell-${i}-${j}`}
                          className={`flex-1 aspect-square min-w-[8px] ${isPeak ? "ring-1 ring-white" : ""}`}
                          style={{
                            backgroundColor:
                              count === 0
                                ? "rgba(255,255,255,0.03)"
                                : `rgba(255, 42, 42, ${0.15 + intensity * 0.85})`,
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {peakDow !== null && (
            <div className="mt-5 flex items-center gap-2" data-testid="heatmap-peak">
              <span className="w-2 h-2 bg-[#FF2A2A] shrink-0" />
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/70">
                peak · {dayLabels[peakDow]} {String(peakHour).padStart(2, "0")}
                :00 {tzLabel} · {max} download{max === 1 ? "" : "s"} · last {days}d
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * CampaignUrlBuilder — assembles a UTM-tagged URL you can paste into
 * Instagram bio, tweets, newsletter, etc. Result is copied to clipboard.
 */
function CampaignUrlBuilder() {
  const [baseUrl, setBaseUrl] = useState("https://stableish.me/");
  const [utmSource, setUtmSource] = useState("instagram");
  const [utmMedium, setUtmMedium] = useState("bio");
  const [utmCampaign, setUtmCampaign] = useState("");

  const buildUrl = () => {
    try {
      const url = new URL(baseUrl);
      if (utmSource.trim()) url.searchParams.set("utm_source", utmSource.trim());
      if (utmMedium.trim()) url.searchParams.set("utm_medium", utmMedium.trim());
      if (utmCampaign.trim()) url.searchParams.set("utm_campaign", utmCampaign.trim());
      return url.toString();
    } catch {
      return baseUrl;
    }
  };

  const finalUrl = buildUrl();

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(finalUrl);
      toast.success("Campaign URL copied.");
    } catch {
      toast.error("Copy failed — select and copy manually.");
    }
  };

  const preset = (source, medium) => () => {
    setUtmSource(source);
    setUtmMedium(medium);
  };

  return (
    <div
      className="border border-white/10 bg-[#0a0a0a] p-6"
      data-testid="analytics-url-builder"
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1">
        Campaign URL builder
      </div>
      <p className="text-xs text-white/50 mb-5 leading-relaxed">
        Tag any link you share so you can measure which posts drive downloads.
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        <FieldGroup label="Base URL" testId="url-base">
          <input
            type="url"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            className="w-full bg-black border border-white/15 focus:border-[#FF2A2A] px-3 py-2 font-mono text-xs text-white placeholder-white/30 focus:outline-none"
            data-testid="url-base-input"
          />
        </FieldGroup>
        <FieldGroup label="utm_source" testId="url-source">
          <input
            type="text"
            value={utmSource}
            onChange={(e) => setUtmSource(e.target.value)}
            placeholder="e.g. instagram"
            className="w-full bg-black border border-white/15 focus:border-[#FF2A2A] px-3 py-2 font-mono text-xs text-white placeholder-white/30 focus:outline-none"
            data-testid="url-source-input"
          />
        </FieldGroup>
        <FieldGroup label="utm_medium" testId="url-medium">
          <input
            type="text"
            value={utmMedium}
            onChange={(e) => setUtmMedium(e.target.value)}
            placeholder="e.g. bio · post · dm"
            className="w-full bg-black border border-white/15 focus:border-[#FF2A2A] px-3 py-2 font-mono text-xs text-white placeholder-white/30 focus:outline-none"
            data-testid="url-medium-input"
          />
        </FieldGroup>
        <FieldGroup label="utm_campaign (optional)" testId="url-campaign">
          <input
            type="text"
            value={utmCampaign}
            onChange={(e) => setUtmCampaign(e.target.value)}
            placeholder="e.g. launch · week1"
            className="w-full bg-black border border-white/15 focus:border-[#FF2A2A] px-3 py-2 font-mono text-xs text-white placeholder-white/30 focus:outline-none"
            data-testid="url-campaign-input"
          />
        </FieldGroup>
      </div>

      {/* Presets */}
      <div className="mt-4 flex flex-wrap gap-2">
        <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-white/35 self-center">
          quick set:
        </span>
        {[
          ["instagram", "bio"],
          ["instagram", "story"],
          ["twitter", "post"],
          ["reddit", "post"],
          ["newsletter", "email"],
          ["direct", "print"],
        ].map(([s, m]) => (
          <button
            key={`${s}-${m}`}
            onClick={preset(s, m)}
            className="font-mono text-[10px] uppercase tracking-[0.18em] border border-white/10 hover:border-[#FF2A2A]/60 hover:text-[#FF2A2A] text-white/60 px-2 py-1 transition-colors"
            data-testid={`url-preset-${s}-${m}`}
          >
            {s} · {m}
          </button>
        ))}
      </div>

      {/* Preview + copy */}
      <div className="mt-5 border border-[#FF2A2A]/30 bg-[#FF2A2A]/[0.04] p-4">
        <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#FF2A2A] mb-2">
          your link
        </div>
        <div className="flex items-start gap-3">
          <code
            className="flex-1 font-mono text-xs text-white/90 break-all leading-relaxed"
            data-testid="url-preview"
          >
            {finalUrl}
          </code>
          <button
            onClick={onCopy}
            data-testid="url-copy"
            className="shrink-0 bg-[#FF2A2A] hover:bg-[#FF4D4D] text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors"
          >
            Copy
          </button>
        </div>
      </div>

      {/* Short-link reference */}
      <div className="mt-6 pt-6 border-t border-white/10" data-testid="url-shortlinks">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 mb-2">
          Short links · always redirect to the App Store
        </div>
        <p className="text-xs text-white/50 mb-3 leading-relaxed">
          Paste these into your Instagram bio, tweets, etc. Each fires a
          tracking beacon before redirecting so you see them in your analytics.
        </p>
        <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 font-mono text-[11px]">
          {[
            ["/ig", "Instagram bio"],
            ["/ig-story", "Instagram story"],
            ["/fb", "Facebook"],
            ["/tt", "TikTok"],
            ["/tw", "Twitter / X"],
            ["/rd", "Reddit"],
            ["/nl", "Newsletter"],
            ["/qr", "Printed QR code"],
          ].map(([path, label]) => (
            <li key={path} className="flex items-center justify-between gap-3">
              <a
                href={path}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/85 hover:text-[#FF2A2A]"
              >
                stableish.me{path}
              </a>
              <span className="text-white/40 text-[10px] uppercase tracking-[0.18em]">
                {label}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/**
 * InstallsSection — real App Store installs pulled from Apple's App Store
 * Connect Sales Reports API. Cached in Mongo; refresh with a button click.
 */
function InstallsSection({ installs, onRefresh, refreshing, onSendDigestPreview }) {
  if (!installs) return null;

  const configured = installs.configured;
  const lifetime = installs.lifetime ?? 0;
  const last7d = installs.last_7d ?? 0;
  const yesterday = installs.yesterday ?? 0;
  const series = installs.daily_series ?? [];
  const lastFetched = installs.last_fetched_at;
  const maxSeries = Math.max(1, ...series.map((d) => d.installs));

  return (
    <div data-testid="admin-installs">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#FF2A2A] mb-1">
            // real installs · from apple
          </div>
          <h3 className="font-display text-xl font-extrabold tracking-tight">
            Downloads
          </h3>
        </div>
        <div className="flex items-center gap-3">
          {lastFetched && (
            <span
              className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/40"
              data-testid="installs-last-fetched"
            >
              synced {formatDate(lastFetched)}
            </span>
          )}
          <button
            onClick={onRefresh}
            disabled={refreshing || !configured}
            data-testid="installs-refresh"
            className="border border-white/15 hover:border-[#FF2A2A]/60 hover:text-[#FF2A2A] disabled:opacity-40 disabled:cursor-not-allowed text-white/85 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors inline-flex items-center gap-2"
          >
            <ArrowClockwise size={12} weight="bold" className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Syncing…" : "Sync from Apple"}
          </button>
          <button
            onClick={onSendDigestPreview}
            data-testid="installs-digest-preview"
            title="Email a daily-digest preview to the owner"
            className="border border-white/15 hover:border-white/40 text-white/85 hover:text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors inline-flex items-center gap-2"
          >
            <PaperPlaneTilt size={12} weight="bold" />
            Email digest now
          </button>
        </div>
      </div>

      {!configured ? (
        <div
          className="border border-[#FF2A2A]/30 bg-[#FF2A2A]/[0.05] p-6"
          data-testid="installs-not-configured"
        >
          <div className="flex items-start gap-3">
            <Warning size={20} weight="duotone" className="text-[#FF2A2A] shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-white/85 font-semibold">
                Apple App Store Connect not configured
              </p>
              <p className="mt-1.5 text-xs text-white/60 leading-relaxed">
                Set <code className="font-mono text-[#FF2A2A]">APPLE_ISSUER_ID</code>,{" "}
                <code className="font-mono text-[#FF2A2A]">APPLE_KEY_ID</code>,{" "}
                <code className="font-mono text-[#FF2A2A]">APPLE_PRIVATE_KEY</code>,{" "}
                <code className="font-mono text-[#FF2A2A]">APPLE_VENDOR_NUMBER</code>, and{" "}
                <code className="font-mono text-[#FF2A2A]">APPLE_APP_ID</code> env vars on the backend.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-3 gap-4">
            <StatCard
              icon={<AppleLogo size={20} weight="fill" />}
              label="Total installs"
              value={lifetime}
              testId="installs-total"
              accent={lifetime > 0}
            />
            <StatCard
              icon={<DownloadSimple size={20} weight="duotone" />}
              label="Last 7 days"
              value={last7d}
              testId="installs-week"
            />
            <StatCard
              icon={<DownloadSimple size={20} weight="duotone" />}
              label="Yesterday"
              value={yesterday}
              testId="installs-yesterday"
            />
          </div>

          {series.length > 0 && (
            <div className="mt-4 border border-white/10 bg-[#0a0a0a] p-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 mb-4">
                Installs · last 30 days
              </div>
              <div className="flex items-end gap-1 h-32">
                {series.map((d) => {
                  const h = Math.max(4, (d.installs / maxSeries) * 100);
                  return (
                    <div
                      key={d.date}
                      className="flex-1 flex flex-col items-center gap-1 group"
                      title={`${d.date}: ${d.installs} install${d.installs === 1 ? "" : "s"}`}
                    >
                      <div className="w-full flex items-end h-24">
                        <div
                          className="w-full bg-white group-hover:bg-white/80 transition-colors"
                          style={{ height: `${h}%` }}
                        />
                      </div>
                      <span className="font-mono text-[8px] text-white/35">
                        {d.date.slice(5)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-white/35">
            {installs.note ?? "apple's sales reports are delayed 24-48h"}
          </p>
        </>
      )}
    </div>
  );
}
